import { Menu, Search, X } from "lucide-react";
import { useT } from "../../i18n/I18nProvider";
import { userCanPlatform, type StaffUser } from "../../lib/auth";
import { BrandLogo } from "../../components/BrandLogo";
import { AccountMenu } from "./AccountMenu";
import { AlertsMenu } from "./AlertsMenu";
import { MailMenu } from "./MailMenu";
import { PopHost } from "./Pop";
import { HeaderTimer } from "../timer";

/**
 * The header strip: brand, search, and the three things that are about the
 * operator rather than about the page - mail, alerts, account.
 *
 * The brand block is exactly as wide as the sidebar and shrinks with it, so the
 * logo and the rail stay one column instead of drifting apart when the rail
 * collapses. At that width the wordmark gives way to the square mark (`mark`
 * = "auto"): 58px is not enough column for a logo with words in it. Theme and
 * language are not here; they live inside the account menu as switches (see
 * AccountMenu).
 *
 * `menu` is handed in only at drawer widths, where the sidebar has left the
 * page and this button is the one way back to it. On a wide screen the rail is
 * standing there already and the button would be a second door to an open room.
 *
 * The row must never be wider than the screen: a phone handed a wider page
 * zooms the whole page out. What gives way first as it narrows - the search
 * placeholder, the names, the logo, the clock's dot - is in `crm-topbar.css`.
 */
export function TopBar({
  user,
  onLogout,
  onSearch,
  menu,
}: {
  user: StaffUser;
  onLogout: () => void;
  onSearch: () => void;
  menu?: { open: boolean; toggle: () => void };
}) {
  const t = useT();
  const mac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

  return (
    <PopHost>
      <header className="crm-top">
        {menu ? (
          <button
            type="button"
            className="crm-burger"
            onClick={menu.toggle}
            aria-expanded={menu.open}
            aria-controls="crm-nav"
            aria-label={t(menu.open ? "app.menuClose" : "app.menuOpen")}
          >
            {menu.open ? <X className="size-[19px]" /> : <Menu className="size-[19px]" />}
          </button>
        ) : null}

        <div className="crm-brand">
          <BrandLogo className="h-[17px] w-auto text-foreground" mark="auto" />
          <span className="crm-chip-brand max-sm:hidden">{t("app.badge")}</span>
        </div>

        <button type="button" className="crm-search" onClick={onSearch}>
          <Search className="size-4" />
          <span className="crm-trunc flex-1 text-left max-sm:hidden">{t("palette.placeholder")}</span>
          <span className="crm-kbd max-sm:hidden">{mac ? "⌘K" : "Ctrl K"}</span>
        </button>

        <div className="crm-actions flex flex-none items-center">
          {/* First. It is the one control here that is about what the operator
              is DOING rather than about what has arrived for them, and a timer
              that is only visible on the page it was started from is a timer
              that runs all night. It stands whether or not a clock is running,
              because it is also the only way to START one without first going
              to find the task. */}
          <HeaderTimer />
          {userCanPlatform(user, "view:crm") ? <MailMenu /> : null}
          {/* The bell needs the id: what has been cleared is now stored per
              operator rather than per browser profile. */}
          <AlertsMenu userId={user.id} />
          <AccountMenu user={user} onLogout={onLogout} />
        </div>
      </header>
    </PopHost>
  );
}
